/**
 * CDR Hub API Client
 *
 * Unified API layer for all microservices:
 * - auth (9101): Authentication, users, API keys
 * - search (9102): CDR search across all operators
 * - analytics (9103): Dashboard, stats
 * - investigation (9104): Flagged numbers, patterns
 * - ai (9105): LLM analysis, learning
 * - workflow (9106): Automation, sync jobs
 */

import axios from 'axios';

// ============================================================================
// Configuration
// ============================================================================

const TOKEN_KEY = 'authToken';

// Global API error listener (ToastContext subscribes to this)
let _onApiError = null;
export const onApiError = (cb) => { _onApiError = cb; };

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('sessionExpiry');
  localStorage.removeItem('user');
};

// Create axios instance with interceptors
const createClient = (timeout = 60000) => {
  const client = axios.create({
    baseURL: '',
    timeout,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        clearToken();
        window.location.href = '/login';
      } else if (error.response?.status >= 500) {
        const msg = error.response?.data?.error || 'Server error — please try again';
        if (_onApiError) _onApiError(msg);
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        if (_onApiError) _onApiError('Request timed out — try a smaller date range');
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Clients with different timeouts
const api = createClient(60000);
const longApi = createClient(180000); // For AI/search operations

// ============================================================================
// Auth Service (Port 9101)
// ============================================================================

export const auth = {
  login: (username, password) =>
    api.post('/api/login', { username, password }),

  logout: () =>
    api.post('/api/auth/logout').catch(() => {}),

  getMe: () =>
    api.get('/api/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    api.put('/api/auth/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  // User management (admin)
  getUsers: () => api.get('/api/auth/users'),
  getUser: (id) => api.get(`/api/auth/users/${id}`),
  createUser: (data) => api.post('/api/auth/users', data),
  updateUser: (id, data) => api.put(`/api/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/api/auth/users/${id}`),
  resetPassword: (id, newPassword) => api.post(`/api/auth/users/${id}/reset-password`, { new_password: newPassword }),

  // API keys
  getApiKeys: () => api.get('/api/auth/api-keys'),
  createApiKey: (data) => api.post('/api/auth/api-keys', data),
  revokeApiKey: (id) => api.delete(`/api/auth/api-keys/${id}`),
};

// ============================================================================
// Search Service (Port 9102)
// ============================================================================

export const search = {
  // Unified search across all operators
  unified: (params) =>
    longApi.get('/api/unified/search', { params }),

  unifiedStats: () =>
    api.get('/api/unified/stats'),

  // Bulk MSISDN investigation summary (paste lists)
  bulkLookup: (body) =>
    longApi.post('/api/unified/bulk-lookup', body),

  // Operator-specific
  vodacom: (params) => longApi.get('/api/vodacom/search', { params }),
  vodacomHss: (params) => longApi.get('/api/vodacom/hss/search', { params }),
  vodacomPgp: (params) => longApi.get('/api/vodacom/pgp/search', { params }),
  orange: (params) => longApi.get('/api/orange/search', { params }),
  airtel: (params) => longApi.get('/api/airtel/search', { params }),
  africel: (params) => longApi.get('/api/africel/search', { params }),
  africell: (params) => longApi.get('/api/africell/search', { params }),

  // IMSI lookup
  imsi: (params) => api.get('/api/imsi/search', { params }),

  // Federated (all operators)
  federated: (params) => longApi.get('/api/federated/search', { params }),

  // Rwanda-specific
  rwanda: (params) => api.get('/api/rwanda/search', { params }),
};

// ============================================================================
// Tower/Geolocation Service (via Search Service)
// ============================================================================

export const towers = {
  // Lookup tower location by cell info
  lookup: (params) => api.get('/api/towers/lookup', { params }),

  // Lookup most-recent / home tower for a phone (B-party cross-reference)
  phone: (number) => api.get('/api/towers/phone', { params: { number } }),

  // Get tower database stats
  stats: () => api.get('/api/towers/stats'),
};

// ============================================================================
// Analytics Service (Port 9103)
// ============================================================================

export const analytics = {
  dashboard: (params) =>
    api.get('/api/dashboard', { params }),

  stats: () =>
    api.get('/api/stats'),

  operatorStats: () =>
    api.get('/api/stats/operators'),

  trends: (params) =>
    api.get('/api/dashboard/trends', { params }),

  topNumbers: (params) =>
    api.get('/api/dashboard/top-numbers', { params }),

  rwandaAnalytics: (params) =>
    api.get('/api/rwanda/analytics', { params }),
};

// ============================================================================
// Investigation Service (Port 9104)
// ============================================================================

export const investigation = {
  // Number investigation
  investigate: (phone) =>
    api.get('/api/investigate/number', { params: { phone } }),

  // Flagged numbers
  getFlagged: (params) =>
    api.get('/api/investigate/flagged', { params }),

  flag: (phone, reason, flaggedBy) =>
    api.post('/api/investigate/flag', { phone, reason, flagged_by: flaggedBy }),

  unflag: (phone) =>
    api.delete(`/api/investigate/unflag/${phone}`),

  // Patterns
  getPatterns: (params) =>
    api.get('/api/investigate/patterns', { params }),

  // Rwanda
  rwandaPatterns: (params) =>
    api.get('/api/rwanda/patterns', { params }),

  rwandaFlagged: (params) =>
    api.get('/api/rwanda/flagged-numbers', { params }),

  // Network graph
  networkGraph: (phone, params) =>
    api.get(`/api/investigate/network/${phone}`, { params }),
};

// ============================================================================
// AI Service (Port 9105)
// ============================================================================

export const ai = {
  // Analysis
  analyze: (data) =>
    longApi.post('/api/ai/analyze', data),

  analyzeCdr: (cdrId) =>
    longApi.post(`/api/cdr/analyze/${cdrId}`),

  analyzeBulk: (data) =>
    longApi.post('/api/ai/analyze/bulk', data),

  // LLM Chat
  chat: (message, context) =>
    longApi.post('/api/llm/chat', { message, context }),

  // LLM Generate - for direct prompts
  generate: (prompt, system) =>
    longApi.post('/api/llm/generate', { prompt, system }),

  // LLM Analyze - for data analysis
  llmAnalyze: (data, prompt) =>
    longApi.post('/api/llm/analyze', { data, prompt }),

  autoAnalyze: (data) =>
    longApi.post('/api/llm/auto-analyze', data),

  suggestNextSteps: (data) =>
    api.post('/api/llm/suggest-next-steps', data),

  // Feedback & Learning
  submitFeedback: (data) =>
    api.post('/api/feedback/submit', data),

  confirmThreat: (data) =>
    api.post('/api/threat/confirm', data),

  getLearningStats: () =>
    api.get('/api/learning/stats'),

  // Activity logging (non-blocking)
  logActivity: (data) =>
    api.post('/api/activity/log', data).catch(() => {}),
};

// ============================================================================
// Workflow Service (Port 9106)
// ============================================================================

export const workflow = {
  // Workflows
  list: () => api.get('/api/workflows'),
  get: (id) => api.get(`/api/workflows/${id}`),
  create: (data) => api.post('/api/workflows', data),
  update: (id, data) => api.put(`/api/workflows/${id}`, data),
  delete: (id) => api.delete(`/api/workflows/${id}`),
  trigger: (id, input) => api.post(`/api/workflows/${id}/trigger`, { input }),

  // Instances
  getInstances: (params) => api.get('/api/instances', { params }),
  getInstance: (id) => api.get(`/api/instances/${id}`),
  cancelInstance: (id) => api.post(`/api/instances/${id}/cancel`),
  retryInstance: (id) => api.post(`/api/instances/${id}/retry`),

  // Sync jobs
  getSyncJobs: () => api.get('/api/sync/jobs'),
  getSyncJob: (id) => api.get(`/api/sync/jobs/${id}`),
  createSyncJob: (data) => api.post('/api/sync/jobs', data),
  updateSyncJob: (id, data) => api.put(`/api/sync/jobs/${id}`, data),
  deleteSyncJob: (id) => api.delete(`/api/sync/jobs/${id}`),
  runSyncJob: (id) => api.post(`/api/sync/jobs/${id}/run`),
  getSyncHistory: (id) => api.get(`/api/sync/jobs/${id}/history`),
  testConnection: (data) => api.post('/api/sync/test-connection', data),

  // Events
  publishEvent: (data) => api.post('/api/events', data),
  getEvents: (params) => api.get('/api/events', { params }),

  // Stats
  getStats: () => api.get('/api/stats'),
};

// ============================================================================
// Legacy Compatibility Exports
// ============================================================================

// Search aliases
export const searchUnifiedCache = (params) => search.unified(params);
export const getUnifiedStats = () => search.unifiedStats();
export const watchPhone = (phone) => api.post('/api/cache/watch', { phone }).catch(() => {});
export const fetchCdrAnalytics = (params) => analytics.dashboard(params);
export const searchCDRs = (params) => search.unified(params);
export const searchVodacomHss = (params) => search.vodacomHss(params);
export const browseVodacomHss = (params) => search.vodacomHss(params);
export const searchOrangeData = (params) => search.orange(params);
export const searchOrangeVoice = (params) => search.orange(params);
export const searchOrangeSms = (params) => search.orange(params);
export const searchAirtel = (params) => search.airtel(params);
export const searchFederated = (params) => search.federated(params);
export const fetchStats = () => analytics.operatorStats();
export const fetchLatestCDRs = () => search.unified({ page: 1, page_size: 10 });
export const fetchCdrSummary = () => analytics.stats();

// Analytics aliases
export const fetchNetworkFailurePredictions = (params) => analytics.trends(params);

// Investigation aliases
export const getRwandaAnalytics = (params) => analytics.rwandaAnalytics(params);
export const flagRwandaPatterns = (data) => investigation.flag(data.phone, data.reason, data.flagged_by);
export const getFlaggedNumbers = (params) => investigation.getFlagged(params);
export const searchRwandaCalls = (params) => search.rwanda(params);

// AI aliases
export const analyzeCNSCall = (uuid) => ai.analyze({ uuid });
export const analyzeOperatorCDR = (cdrId) => ai.analyzeCdr(cdrId);
export const transcribeCall = (uuid) => ai.analyze({ uuid, transcribe: true });
export const getPhoneAnalysis = (phone) => ai.analyze({ phone });
export const autoAnalyzeSearch = (data) => ai.autoAnalyze(data);
export const chatWithLLM = async (message, context, userId, sessionId) => {
  const response = await ai.chat(message, context);
  return { answer: response.data?.response || response.data?.content || 'No response' };
};
export const getSuggestedNextSteps = async (context, userId) => {
  try {
    const response = await ai.suggestNextSteps({ context, user_id: userId });
    return { success: true, suggestions: response.data?.suggestions || [] };
  } catch (err) {
    return { success: false, suggestions: [] };
  }
};
export const submitFeedback = (data) => ai.submitFeedback(data);
export const logActivity = (data) => ai.logActivity(data);
export const getCountryAnalysis = (params) => analytics.rwandaAnalytics(params);
export const getIntelligenceReport = (params) => analytics.dashboard(params);
export const exportIntelligenceCSV = (params) => analytics.dashboard({ ...params, format: 'csv' });

// Auth aliases (for Settings/UserManagement)
export const userService = {
  getAll: () => auth.getUsers(),
  get: (id) => auth.getUser(id),
  create: (data) => auth.createUser(data),
  update: (id, data) => auth.updateUser(id, data),
  delete: (id) => auth.deleteUser(id),
  resetPassword: (id, password) => auth.resetPassword(id, password),
  getMe: () => auth.getMe(),
  updateProfile: (data) => auth.updateMe?.(data) || Promise.resolve(),
  changePassword: (current, newPass) => auth.changePassword(current, newPass),
};

export const apiKeyService = {
  getAll: () => auth.getApiKeys(),
  create: (data) => auth.createApiKey(data),
  revoke: (id) => auth.revokeApiKey(id),
};

// Vodacom specific
export const VodacomRecordType = { VOICE: 'voice', SMS: 'sms', DATA: 'data', ALL: 'all' };
export const searchVodacomAll = (params) => search.vodacom(params);
export const getVodacomStats = () => search.unifiedStats();
export const getVodacomAllStats = () => search.unifiedStats();

// Case management (maps to investigation service)
export const getCases = (params) => api.get('/api/cases', { params });
export const createCase = (data) => api.post('/api/cases', data);
export const updateCase = (id, data) => api.put(`/api/cases/${id}`, data);
export const getCase = (id) => api.get(`/api/cases/${id}`);
export const closeCase = (id, outcome) => api.post(`/api/cases/${id}/close`, { outcome });
export const addCaseNote = (id, note) => api.post(`/api/cases/${id}/notes`, { note });
export const deleteCase = (id) => api.delete(`/api/cases/${id}`);
export const addCaseTarget = (caseId, target) => api.post(`/api/cases/${caseId}/targets`, target);
export const removeCaseTarget = (caseId, targetId) => api.delete(`/api/cases/${caseId}/targets/${targetId}`);

// Target management
export const getTargets = (params) => api.get('/api/targets', { params });
export const createTarget = (data) => api.post('/api/targets', data);
export const updateTarget = (id, data) => api.put(`/api/targets/${id}`, data);
export const deleteTarget = (id) => api.delete(`/api/targets/${id}`);
export const getTargetProfile = (id) => api.get(`/api/targets/${id}`);
export const associateTarget = (targetId, caseId) => api.post('/api/targets/associate', { target_id: targetId, case_id: caseId });

// Pattern analysis
export const analyzePatterns = (data) => longApi.post('/api/patterns/analyze', data);
export const getPatternHistory = (phone) => api.get(`/api/patterns/history/${phone}`);

// OSINT
export const webOsint = (query) => longApi.post('/api/osint/web', { query });
export const socialOsint = (query) => longApi.post('/api/osint/social', { query });
export const comprehensiveOsint = (query) => longApi.post('/api/osint/comprehensive', { query });
export const comprehensiveOSINT = (query) => longApi.post('/api/osint/comprehensive', { query });
export const getOsintHistory = (targetId) => api.get(`/api/osint/history/${targetId}`);

// Threat assessment
export const assessThreat = (data) => api.post('/api/threat/assess', data);
export const getThreatScore = (phone) => api.get(`/api/threat/score/${phone}`);

// Export/PDF
export const generatePDF = (data) => longApi.post('/api/export/pdf', data, { responseType: 'blob' });
export const downloadExport = (filename) => api.get(`/api/export/download/${filename}`, { responseType: 'blob' });
export const getExportHistory = () => api.get('/api/export/history');

// ============================================================================
// Default Export
// ============================================================================

export default { auth, search, analytics, investigation, ai, workflow };
